/**
 * Brand themes (Admin → Brands → Theme).
 *
 * RiskLo's stylesheets use a fixed set of purples, indigos and navy
 * backgrounds. Each of those is written as var(--bt-<hex>, <hex>), so with no
 * theme nothing changes. A brand theme fills those variables.
 *
 * Why it looks right with almost any colors: every original shade is
 * replaced by the brand's hue and saturation at the SAME luminance as the
 * original. Every text-on-color and color-on-background contrast in the
 * design therefore stays what it was, whether the brand is gold, green or red.
 *
 * Families:
 *   primary    — RiskLo indigo (#667eea and friends): buttons, links, active items
 *   secondary  — RiskLo violet (#764ba2 / #8b5cf6 …): gradient ends, highlights
 *   background — RiskLo navy (#0a0e27 …): page and panel backgrounds (dark mode)
 * Accent is used directly for a few small highlights (PRO badge, brand title).
 *
 * style 'mono' (black & white): every family becomes neutral gray at the same
 * luminance, so dark mode is near-black with white text and light mode is white
 * with near-black buttons; small highlights are white with dark text.
 */

import { setMonoSurfaces } from './brandMonoSurfaces';
import { applyBrandFonts } from './brandFonts';

const FAMILIES = {
  primary: {
    ref: '#667eea',
    colors: [
      '#667eea', '#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe', '#e0e7ff', '#eef2ff',
      '#4f46e5', '#4338ca', '#312e81', '#1e1b4b', '#6f6dd8', '#8b9cff', '#5c60ff',
    ],
  },
  secondary: {
    ref: '#8b5cf6',
    colors: [
      '#764ba2', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#f5f3ff',
      '#7c3aed', '#6d28d9', '#5b21b6', '#4c1d95', '#a855f7', '#9333ea', '#7e22ce',
    ],
  },
  background: {
    ref: '#0a0e27',
    colors: ['#0a0e27', '#1a1f3a', '#1a1a2e', '#090e1d', '#0d1224', '#0b1020'],
  },
};

/** Every original color the stylesheets route through a --bt-* variable. */
export const THEMED_COLORS = Object.values(FAMILIES).flatMap((f) => f.colors);

export const HEX_RE = /^#[0-9a-f]{6}$/i;

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex([r, g, b]) {
  return `#${[r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;
}

function rgbToHsl([r, g, b]) {
  const rn = r / 255; const gn = g / 255; const bn = b / 255;
  const max = Math.max(rn, gn, bn); const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h * 60, s, l];
}

function hslToRgb([h, s, l]) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rgb;
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return rgb.map((v) => (v + m) * 255);
}

/** WCAG relative luminance. */
export function luminance(hex) {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Brand hue/saturation at the given luminance (binary search on lightness). */
function atLuminance(h, s, target) {
  let lo = 0; let hi = 1;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (luminance(rgbToHex(hslToRgb([h, s, mid]))) < target) lo = mid; else hi = mid;
  }
  return rgbToHex(hslToRgb([h, s, (lo + hi) / 2]));
}

/**
 * Re-color one family: each original keeps its luminance and its saturation
 * relative to the family's reference color, and takes the brand's hue.
 */
function recolor(family, brandHex, satCap = 1) {
  const [h, brandS] = rgbToHsl(hexToRgb(brandHex));
  const [, refS] = rgbToHsl(hexToRgb(family.ref));
  const out = {};
  family.colors.forEach((orig) => {
    const [, origS] = rgbToHsl(hexToRgb(orig));
    const s = Math.min(satCap, Math.max(0, brandS * (refS ? origS / refS : 1)), 1);
    out[orig] = atLuminance(h, s, luminance(orig));
  });
  return out;
}

/** Neutral gray at each original's luminance (black & white themes). */
function neutral(family) {
  const out = {};
  family.colors.forEach((orig) => { out[orig] = atLuminance(0, 0, luminance(orig)); });
  return out;
}

/** The fixed primary color stored for black & white themes (keeps old readers working). */
export const MONO_PRIMARY = '#737373';

/** Normalize what an admin typed: '#AbC123' → '#abc123', else null. */
export function cleanHex(value) {
  const v = String(value || '').trim();
  const withHash = v.startsWith('#') ? v : `#${v}`;
  return HEX_RE.test(withHash) ? withHash.toLowerCase() : null;
}

/**
 * CSS variables for a theme, or {} when it's off or has no primary color.
 * theme: { enabled, primary, secondary?, accent?, background? } (hex strings)
 */
export function buildThemeVars(theme) {
  if (!theme || theme.enabled === false) return {};
  if (theme.style === 'mono') {
    const mapped = { ...neutral(FAMILIES.primary), ...neutral(FAMILIES.secondary), ...neutral(FAMILIES.background) };
    const vars = {};
    Object.entries(mapped).forEach(([orig, hex]) => {
      const key = orig.slice(1).toLowerCase();
      vars[`--bt-${key}`] = hex;
      vars[`--bt-${key}-rgb`] = hexToRgb(hex).join(', ');
    });
    vars['--bt-accent'] = '#f5f5f5';
    vars['--bt-accent-rgb'] = '245, 245, 245';
    vars['--bt-accent-text'] = '#0b0f19';
    return vars;
  }
  const primary = cleanHex(theme.primary);
  if (!primary) return {};
  const secondary = cleanHex(theme.secondary) || primary;
  const background = cleanHex(theme.background) || primary;

  const mapped = {
    ...recolor(FAMILIES.primary, primary),
    ...recolor(FAMILIES.secondary, secondary),
    // Backgrounds stay calm even for loud brand colors.
    ...recolor(FAMILIES.background, background, cleanHex(theme.background) ? 1 : 0.45),
  };
  const vars = {};
  Object.entries(mapped).forEach(([orig, hex]) => {
    const key = orig.slice(1).toLowerCase();
    vars[`--bt-${key}`] = hex;
    vars[`--bt-${key}-rgb`] = hexToRgb(hex).join(', ');
  });

  // Accent: used as-is for small highlights, with readable text on top.
  const accent = cleanHex(theme.accent) || mapped['#8b5cf6'];
  vars['--bt-accent'] = accent;
  vars['--bt-accent-rgb'] = hexToRgb(accent).join(', ');
  vars['--bt-accent-text'] = contrast(accent, '#ffffff') >= contrast(accent, '#0b0f19') ? '#ffffff' : '#0b0f19';
  return vars;
}

const APPLIED_KEY = '__riskloBrandThemeVars';

/** Apply (or clear, with null) a brand theme on the whole document. */
export function applyBrandTheme(theme, root = (typeof document !== 'undefined' ? document.documentElement : null)) {
  if (!root) return;
  (root[APPLIED_KEY] || []).forEach((name) => root.style.removeProperty(name));
  const vars = buildThemeVars(theme);
  const names = Object.keys(vars);
  names.forEach((name) => root.style.setProperty(name, vars[name]));
  root[APPLIED_KEY] = names;
  if (names.length) root.setAttribute('data-brand-theme', 'on');
  else root.removeAttribute('data-brand-theme');
  const mono = Boolean(names.length && theme?.style === 'mono');
  if (mono) root.setAttribute('data-brand-style', 'mono');
  else root.removeAttribute('data-brand-style');
  if (root === document.documentElement) setMonoSurfaces(mono);
}

/**
 * Suggest theme colors from a logo (data URL or same-origin URL): the most
 * common clearly-colored pixels, skipping white, black and grays.
 */
export function suggestFromImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const size = 96;
        const canvas = document.createElement('canvas');
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        const buckets = new Map();
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 200) continue;
          const [h, s, l] = rgbToHsl([data[i], data[i + 1], data[i + 2]]);
          if (s < 0.25 || l < 0.08 || l > 0.92) continue;
          const key = `${Math.round(h / 15)}`;
          const b = buckets.get(key) || { n: 0, r: 0, g: 0, b: 0 };
          b.n += 1; b.r += data[i]; b.g += data[i + 1]; b.b += data[i + 2];
          buckets.set(key, b);
        }
        const colors = [...buckets.values()]
          .sort((a, b) => b.n - a.n)
          .map((b) => rgbToHex([b.r / b.n, b.g / b.n, b.b / b.n]));
        // No colored pixels at all: a black, white or gray logo (suits the black & white style).
        resolve({ primary: colors[0] || null, accent: colors[1] || null, colors: colors.slice(0, 4), reason: colors.length ? null : 'monochrome' });
      } catch (_e) {
        resolve({ primary: null, accent: null, colors: [], reason: 'unreadable' });
      }
    };
    img.onerror = () => resolve({ primary: null, accent: null, colors: [], reason: 'unreadable' });
    img.src = src;
  });
}

/**
 * Layout style (Admin → Brands → Identity): a preset class on <body> plus its
 * font, so two brands differ in shape and type, not just color. Classic (or
 * none) is RiskLo's own look. Styles live in styles/brandLayouts.css.
 */
const LAYOUT_FONTS = {
  clean: { family: 'Manrope', css: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap' },
  sharp: { family: 'IBM Plex Sans', css: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap' },
};
const LAYOUT_CLASSES = ['brand-layout-clean', 'brand-layout-sharp'];

/** Load a layout's font without switching the app to it (for previews). */
export function ensureLayoutFont(preset) {
  if (typeof document === 'undefined' || !LAYOUT_FONTS[preset]) return;
  const id = `brand-font-${preset}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = LAYOUT_FONTS[preset].css;
  document.head.appendChild(link);
}

export function applyBrandLayout(layout) {
  if (typeof document === 'undefined') return;
  // The brand's own fonts (Brand Settings → Fonts) ride along with the layout.
  applyBrandFonts(layout?.fonts || null);
  const preset = layout && LAYOUT_FONTS[layout.preset] ? layout.preset : 'classic';
  const targets = [document.documentElement, document.body].filter(Boolean);
  targets.forEach((el) => {
    el.classList.remove(...LAYOUT_CLASSES);
    if (preset !== 'classic') el.classList.add(`brand-layout-${preset}`);
  });
  if (preset === 'classic') return;
  const font = LAYOUT_FONTS[preset];
  const id = `brand-font-${preset}`;
  if (!document.getElementById(id)) {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = font.css;
    document.head.appendChild(link);
  }
}

/** Light or dark for a first visit, when the brand sets one (the user's own choice wins). */
export function brandDefaultMode(brand) {
  const mode = brand?.branding?.layout?.defaultMode;
  return mode === 'light' || mode === 'dark' ? mode : null;
}
